import DetailsCard from "@/components/DetailsCard";
import DetailsCardThreeColumnRow from "@/components/DetailsCardThreeColumnRow";
import PlaceholderCard from "@/components/PlaceholderCard";
import SectionHeading from "@/components/SectionHeading";
import Tag from "@/components/Tag";

import {
  getCareerRoleDisplayName,
  getRoleFitCompatibilityTierLabel,
  getTopCompatibleRoles,
  type CareerRoleFit
} from "./experienceIndexResults";

const TOP_COMPATIBLE_ROLES_HEADING = {
  heading: "Top Compatible Roles",
  body: "Strongest role matches from this person's completed answers. Compatibility is generalized and projected, not guaranteed."
};

const TOP_COMPATIBLE_ROLES_EMPTY_COPY = "Compatible roles will appear here.";

function RoleFitCard({ isComparisonRole, roleFit }: { isComparisonRole: boolean; roleFit: CareerRoleFit }) {
  const displayName = getCareerRoleDisplayName(roleFit);

  return (
    <DetailsCard
      heading={displayName}
      caption={getRoleFitCompatibilityTierLabel(roleFit.fitPercent)}
      testId={`career-plan-top-compatible-role-${roleFit.id}`}
    >
      {isComparisonRole ? (
        <div className="mb-2" data-testid={`career-plan-top-compatible-role-${roleFit.id}-comparison-role-tag`}>
          <Tag tagType="success" tagStyle="standard" size="small">
            Comparison Role
          </Tag>
        </div>
      ) : null}
      <p className="text-title-large-bold text-sys-brand-primary">{roleFit.fitPercent}%</p>
      <p className="mt-2 text-body-small text-sys-brand-on-surface">
        {roleFit.matchedCompetencyCount} of {roleFit.completedCompetencyCount} completed competencies meet this role's
        rubric.
      </p>
    </DetailsCard>
  );
}

export default function ExperienceIndexTopCompatibleRolesSection({
  comparisonRoleId,
  roleFits
}: {
  comparisonRoleId: string;
  roleFits: CareerRoleFit[];
}) {
  const topCompatibleRoles = getTopCompatibleRoles(roleFits);

  return (
    <div data-testid="career-plan-top-compatible-roles">
      <SectionHeading heading={TOP_COMPATIBLE_ROLES_HEADING.heading} body={TOP_COMPATIBLE_ROLES_HEADING.body} />
      {topCompatibleRoles.length > 0 ? (
        <DetailsCardThreeColumnRow>
          {topCompatibleRoles.map((roleFit) => (
            <RoleFitCard key={roleFit.id} isComparisonRole={roleFit.id === comparisonRoleId} roleFit={roleFit} />
          ))}
        </DetailsCardThreeColumnRow>
      ) : (
        <PlaceholderCard
          title={TOP_COMPATIBLE_ROLES_EMPTY_COPY}
          iconName="quiz"
          testId="career-plan-top-compatible-roles-empty"
        />
      )}
    </div>
  );
}
